// greenfield-sortables.js — Greenfield and Vault canvas sortable drag-and-drop logic.
// Extracted from script.js (~717 lines).
//
// Deps: state.js, utils.js, helpers.js, recalc.js
// Window bridges consumed: createSite, buildDefaultJobEntry,
//   buildDefaultUnprotectedWorkloadEntry, syncExistingEnvironmentFromServers,
//   syncProtectionTogglesFromJobs, syncDesignOptionFields, updateTotalInstances,
//   updateWorkloadValidationState, syncNextStepAvailability, updateGfSummaryCount,
//   moveOneLockedCompSlot
// Window bridges set: gfCanvasSortable, gfWlSortables, lockedQtyFieldKeys

import Sortable from 'sortablejs';
import { sites } from './state.js';
import { lockedQtyFieldKeys } from './config.js';
import { isRepoMigrationWorkload } from './utils.js';
import { destroyGreenfieldSortables, setAddSiteDropHover, getReliableDropIndex } from './helpers.js';
import { scheduleRenderEstimate } from './recalc.js';
import { emit, BUS_EVENTS } from './bus.js';

// ── Module-level sortable state ───────────────────────────────────────────────
// Bridged to window so destroyGreenfieldSortables (in helpers.js) can access them.

let gfCanvasSortable = null;
window.gfCanvasSortable = gfCanvasSortable;
const gfWlSortables = [];
window.gfWlSortables = gfWlSortables;

// ── Locked-qty field keys ─────────────────────────────────────────────────────
// Imported from config.js (single source of truth shared with recalc.js).
// Re-exported so script.js can import it by name; bridged to window for
// the Event Wiring and Chip-canvas sections that read it via bare-name access.
export { lockedQtyFieldKeys };
window.lockedQtyFieldKeys = lockedQtyFieldKeys;

// ── Greenfield Sortables ──────────────────────────────────────────────────────

/**
 * Returns the correct insertion index for a SortableJS onEnd drop.
 *
 * With forceFallback: true, evt.newDraggableIndex is 0 for cross-container
 * drops — a known SortableJS bug. By the time onEnd fires, SortableJS has
 * already placed evt.item into evt.to at the correct DOM position, so we
 * can count how many .gf-chip siblings precede it for a reliable index.
 * For same-container reorders the same count gives the correct post-move index.
 */

export function initGreenfieldSortables() {
    destroyGreenfieldSortables();

    const sitesContainer = document.getElementById('gf-sites');
    if (!sitesContainer) return;

    // Site-level reordering — only .gf-site children, no add button in this container
    gfCanvasSortable = Sortable.create(sitesContainer, {
        animation: 150,
        handle: '.gf-site-grip',
        draggable: '.gf-site',
        ghostClass: 'gf-site-ghost',
        chosenClass: 'gf-site-chosen',
        onEnd(evt) {
            if (evt.oldIndex === evt.newIndex) return;
            const [moved] = sites.splice(evt.oldIndex, 1);
            sites.splice(evt.newIndex, 0, moved);
            emit(BUS_EVENTS.SITE_LIST_CHANGED);
        }
    });
    window.gfCanvasSortable = gfCanvasSortable;

    // Workload chip reordering (within and across sites).
    // Sortables are attached to each .gf-chip-group div so that .gf-chip elements
    // are direct children of the sortable container — required by SortableJS for
    // reliable cross-container drag-and-drop.
    // We iterate over the sites array and locate each group via known DOM IDs so
    // this is robust regardless of whether data-gf-site is in the rendered HTML.
    sites.forEach((site) => {
        const zone = document.getElementById('gf-wl-' + site.id);
        if (!zone) return;

        // ── Uncategorised workloads (staging area) ────────────────────────────
        const uncGroup = zone.querySelector('.gf-chip-group--uncategorised');
        if (uncGroup) {
            uncGroup.dataset.gfSite = String(site.id);
            const sortable = Sortable.create(uncGroup, {
                group: { name: 'gf-uncategorised-workloads', put: ['gf-uncategorised-workloads'] },
                animation: 150,
                handle: '.gf-chip-grip',
                draggable: '.gf-chip',
                ghostClass: 'gf-chip-ghost',
                chosenClass: 'gf-chip-chosen',
                forceFallback: true,
                fallbackOnBody: true,
                onEnd(evt) {
                    setAddSiteDropHover(false);
                    const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                    const toSiteId   = parseInt(evt.to.dataset.gfSite, 10);
                    const newIdx = getReliableDropIndex(evt);
                    const oldIdx = evt.oldDraggableIndex ?? evt.oldIndex;
                    const fromSite = sites.find((s) => s.id === fromSiteId);
                    const toSite   = sites.find((s) => s.id === toSiteId);
                    if (!fromSite || !toSite) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    const rowId   = parseInt(evt.item.dataset.gfUncChip, 10);
                    const fromArr = Array.isArray(fromSite.uncategorisedWorkloads) ? fromSite.uncategorisedWorkloads : [];

                    // Cross-group: uncategorised chip dropped into unprotected group
                    if (evt.to.classList.contains('gf-chip-group--wl')) {
                        const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                        if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                        const [wl] = fromArr.splice(fromIdx, 1);
                        const validTo    = toSite.workloads.filter((w) => w.value && !isRepoMigrationWorkload(w.value));
                        const insertAt   = Math.min(newIdx, validTo.length);
                        const firstValid = toSite.workloads.findIndex((w) => w.value && !isRepoMigrationWorkload(w.value));
                        const absInsert  = firstValid === -1 ? toSite.workloads.length : firstValid + insertAt;
                        toSite.workloads.splice(absInsert, 0, wl);
                        emit(BUS_EVENTS.SITE_LIST_CHANGED);
                        return;
                    }

                    // Cross-group: uncategorised chip dropped into protected group
                    if (evt.to.classList.contains('gf-chip-group--protected')) {
                        const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                        if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                        const [wl] = fromArr.splice(fromIdx, 1);
                        if (!Array.isArray(toSite.protectedWorkloads)) toSite.protectedWorkloads = [];
                        toSite.protectedWorkloads.splice(Math.min(newIdx, toSite.protectedWorkloads.length), 0, wl);
                        emit(BUS_EVENTS.SITE_LIST_CHANGED);
                        return;
                    }

                    // Normal: uncategorised chip stays within uncategorised (same or cross-site)
                    if (fromSiteId === toSiteId && oldIdx === newIdx) return;
                    const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                    if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                    const [wl] = fromArr.splice(fromIdx, 1);
                    if (!Array.isArray(toSite.uncategorisedWorkloads)) toSite.uncategorisedWorkloads = [];
                    toSite.uncategorisedWorkloads.splice(Math.min(newIdx, toSite.uncategorisedWorkloads.length), 0, wl);
                    emit(BUS_EVENTS.SITE_LIST_CHANGED);
                }
            });
            gfWlSortables.push(sortable);
        }

        // ── Unprotected workloads ──────────────────────────────────────────────
        const wlGroup = zone.querySelector('.gf-chip-group--wl');
        if (wlGroup) {
            wlGroup.dataset.gfSite = String(site.id); // stamp programmatically
            const sortable = Sortable.create(wlGroup, {
                group: { name: 'gf-workloads', put: ['gf-workloads', 'gf-protected-workloads', 'gf-uncategorised-workloads'] },
                animation: 150,
                handle: '.gf-chip-grip',
                draggable: '.gf-chip',
                ghostClass: 'gf-chip-ghost',
                chosenClass: 'gf-chip-chosen',
                // forceFallback + fallbackOnBody ensure the drag ghost escapes
                // any ancestor with overflow:hidden (e.g. .gf-site) so the
                // chip can be dropped onto a different site card.
                forceFallback: true,
                fallbackOnBody: true,
                onEnd(evt) {
                    setAddSiteDropHover(false);
                    const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                    const toSiteId   = parseInt(evt.to.dataset.gfSite, 10);
                    const newIdx = getReliableDropIndex(evt);
                    const oldIdx = evt.oldDraggableIndex ?? evt.oldIndex;
                    const fromSite = sites.find((s) => s.id === fromSiteId);
                    const toSite   = sites.find((s) => s.id === toSiteId);
                    if (!fromSite || !toSite) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    // Cross-group: unprotected chip dropped INTO the protected group
                    // (onEnd fires on the SOURCE sortable; use evt.to to detect destination)
                    if (evt.to.classList.contains('gf-chip-group--protected')) {
                        const rowId = parseInt(evt.item.dataset.gfChip, 10);
                        const fromIdx = fromSite.workloads.findIndex(
                            (w) => w.rowId === rowId && w.value && !isRepoMigrationWorkload(w.value)
                        );
                        if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                        const [wl] = fromSite.workloads.splice(fromIdx, 1);
                        if (!Array.isArray(toSite.protectedWorkloads)) toSite.protectedWorkloads = [];
                        toSite.protectedWorkloads.splice(Math.min(newIdx, toSite.protectedWorkloads.length), 0, wl);
                        emit(BUS_EVENTS.SITE_LIST_CHANGED);
                        return;
                    }

                    // Normal: unprotected chip stays within unprotected group (same or cross-site)
                    const rowId = parseInt(evt.item.dataset.gfChip, 10);
                    if (fromSiteId === toSiteId && oldIdx === newIdx) return;

                    const fromIdx = fromSite.workloads.findIndex(
                        (w) => w.rowId === rowId && w.value && !isRepoMigrationWorkload(w.value)
                    );
                    if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    const [wl] = fromSite.workloads.splice(fromIdx, 1);
                    const validTo = toSite.workloads.filter((w) => w.value && !isRepoMigrationWorkload(w.value));
                    const insertAt = Math.min(newIdx, validTo.length);
                    const firstValid = toSite.workloads.findIndex((w) => w.value && !isRepoMigrationWorkload(w.value));
                    const absInsert  = firstValid === -1 ? toSite.workloads.length : firstValid + insertAt;
                    toSite.workloads.splice(absInsert, 0, wl);

                    emit(BUS_EVENTS.SITE_LIST_CHANGED);
                }
            });
            gfWlSortables.push(sortable);
        }

        // ── Protected workloads ────────────────────────────────────────────────
        const protGroup = zone.querySelector('.gf-chip-group--protected');
        if (protGroup) {
            protGroup.dataset.gfSite = String(site.id);
            const sortable = Sortable.create(protGroup, {
                group: { name: 'gf-protected-workloads', put: ['gf-workloads', 'gf-protected-workloads', 'gf-uncategorised-workloads'] },
                animation: 150,
                handle: '.gf-chip-grip',
                draggable: '.gf-chip',
                ghostClass: 'gf-chip-ghost',
                chosenClass: 'gf-chip-chosen',
                forceFallback: true,
                fallbackOnBody: true,
                onEnd(evt) {
                    setAddSiteDropHover(false);
                    const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                    const toSiteId   = parseInt(evt.to.dataset.gfSite, 10);
                    const newIdx = getReliableDropIndex(evt);
                    const oldIdx = evt.oldDraggableIndex ?? evt.oldIndex;
                    const fromSite = sites.find((s) => s.id === fromSiteId);
                    const toSite   = sites.find((s) => s.id === toSiteId);
                    if (!fromSite || !toSite) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    // Cross-group: protected chip dropped INTO the unprotected group
                    // (onEnd fires on the SOURCE sortable; use evt.to to detect destination)
                    if (evt.to.classList.contains('gf-chip-group--wl')) {
                        const rowId = parseInt(evt.item.dataset.gfPwChip, 10);
                        const fromArr = Array.isArray(fromSite.protectedWorkloads) ? fromSite.protectedWorkloads : [];
                        const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                        if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                        const [wl] = fromArr.splice(fromIdx, 1);
                        const validTo = toSite.workloads.filter((w) => w.value && !isRepoMigrationWorkload(w.value));
                        const insertAt = Math.min(newIdx, validTo.length);
                        const firstValid = toSite.workloads.findIndex((w) => w.value && !isRepoMigrationWorkload(w.value));
                        const absInsert = firstValid === -1 ? toSite.workloads.length : firstValid + insertAt;
                        toSite.workloads.splice(absInsert, 0, wl);
                        emit(BUS_EVENTS.SITE_LIST_CHANGED);
                        return;
                    }

                    // Normal: protected chip stays within protected group (same or cross-site)
                    const rowId = parseInt(evt.item.dataset.gfPwChip, 10);
                    if (fromSiteId === toSiteId && oldIdx === newIdx) return;

                    const fromArr = Array.isArray(fromSite.protectedWorkloads) ? fromSite.protectedWorkloads : [];
                    const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                    if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    const [wl] = fromArr.splice(fromIdx, 1);
                    if (!Array.isArray(toSite.protectedWorkloads)) toSite.protectedWorkloads = [];
                    toSite.protectedWorkloads.splice(Math.min(newIdx, toSite.protectedWorkloads.length), 0, wl);

                    emit(BUS_EVENTS.SITE_LIST_CHANGED);
                }
            });
            gfWlSortables.push(sortable);
        }

        // ── Veeam Components (within-site reorder; add-site drop allowed) ────────
        const compGroup = zone.querySelector('.gf-chip-group--comp');
        if (compGroup) {
            compGroup.dataset.gfSite = String(site.id);
            const sortable = Sortable.create(compGroup, {
                // pull: true lets comp chips be dragged to the add-site target;
                // put: ['gf-comp-chips'] allows cross-site drops from other comp groups.
                group: { name: 'gf-comp-chips', pull: true, put: ['gf-comp-chips'] },
                animation: 150,
                handle: '.gf-chip-grip',
                draggable: '.gf-chip',
                ghostClass: 'gf-chip-ghost',
                chosenClass: 'gf-chip-chosen',
                forceFallback: true,
                fallbackOnBody: true,
                onStart() { document.body.classList.add('is-comp-dragging'); },
                onEnd(evt) {
                    document.body.classList.remove('is-comp-dragging');
                    setAddSiteDropHover(false);
                    // If the chip left this container (e.g. dropped on add-site btn),
                    // renderSiteList() will have already been called by the target's onAdd.
                    if (evt.to !== compGroup) return;
                    if (evt.oldIndex === evt.newIndex) return;
                    // Within-site reorder: sync veeamServers array order to DOM order.
                    const siteObj = sites.find((s) => s.id === site.id);
                    if (!siteObj || !Array.isArray(siteObj.veeamServers)) return;
                    const chips = Array.from(compGroup.querySelectorAll('.gf-chip[data-gf-server-type-chip]'));
                    if (!chips.length) return;
                    const newTypeOrder = chips.map((c) => c.dataset.gfServerTypeChip);
                    siteObj.veeamServers.sort((a, b) => {
                        const ai = newTypeOrder.indexOf(a.type);
                        const bi = newTypeOrder.indexOf(b.type);
                        if (ai === -1 && bi === -1) return 0;
                        if (ai === -1) return 1;
                        if (bi === -1) return -1;
                        return ai - bi;
                    });
                },
                onAdd(evt) {
                    document.body.classList.remove('is-comp-dragging');
                    setAddSiteDropHover(false);
                    const item       = evt.item;
                    const toSiteId   = parseInt(compGroup.dataset.gfSite, 10);
                    const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                    if (fromSiteId === toSiteId) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                    const toSite   = sites.find((s) => s.id === toSiteId);
                    const fromSite = sites.find((s) => s.id === fromSiteId);
                    if (!toSite || !fromSite) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    if (item.dataset.gfServerTypeChip !== undefined) {
                        // Named server instances (VBR, VB365, Kasten…) — move ALL of this type
                        const typeKey = item.dataset.gfServerTypeChip;
                        if (Array.isArray(fromSite.veeamServers)) {
                            const moved = fromSite.veeamServers.filter((s) => s.type === typeKey);
                            fromSite.veeamServers = fromSite.veeamServers.filter((s) => s.type !== typeKey);
                            if (!Array.isArray(toSite.veeamServers)) toSite.veeamServers = [];
                            toSite.veeamServers.push(...moved);
                            window.syncExistingEnvironmentFromServers(fromSite);
                            window.syncExistingEnvironmentFromServers(toSite);
                        }
                    } else if (item.dataset.gfCompChip !== undefined) {
                        const compKey   = item.dataset.gfCompChip;
                        const version   = item.dataset.gfCompVersion   || null;
                        const haPairId  = item.dataset.gfHaPairId      || null;
                        const slotIndex = item.dataset.gfCompSlotIndex !== undefined
                            ? parseInt(item.dataset.gfCompSlotIndex, 10)
                            : null;
                        if (lockedQtyFieldKeys.has(compKey)) {
                            // Locked-qty field (VBR, VRO, VONE, VB365 repos): move exactly one slot
                            window.moveOneLockedCompSlot(fromSite, toSite, compKey, version, haPairId, slotIndex);
                        } else if (fromSite.existingEnvironment && compKey in fromSite.existingEnvironment) {
                            if (!toSite.existingEnvironment) toSite.existingEnvironment = {};
                            toSite.existingEnvironment[compKey] = fromSite.existingEnvironment[compKey];
                            delete fromSite.existingEnvironment[compKey];
                        }
                    } else if (item.dataset.gfChip !== undefined && isRepoMigrationWorkload(item.dataset.gfChipValue)) {
                        // Vault / repository chip — move workload entry between sites
                        const rowId = parseInt(item.dataset.gfChip, 10);
                        const fromIdx = fromSite.workloads.findIndex(
                            (w) => w.rowId === rowId && isRepoMigrationWorkload(w.value)
                        );
                        if (fromIdx !== -1) {
                            const [wl] = fromSite.workloads.splice(fromIdx, 1);
                            toSite.workloads.push(wl);
                        }
                    }
                    emit(BUS_EVENTS.SITE_LIST_CHANGED);
                }
            });
            gfWlSortables.push(sortable);
        }

        // ── Unprotected workloads ("Currently Unprotected") ────────────────
        const unprotectedGroup = zone.querySelector('.gf-chip-group--unprotected');
        if (unprotectedGroup) {
            unprotectedGroup.dataset.gfSite = String(site.id);
            const sortable = Sortable.create(unprotectedGroup, {
                group: { name: 'gf-unprotected-workloads', put: ['gf-unprotected-workloads', 'gf-jobs'] },
                animation: 150,
                handle: '.gf-chip-grip',
                draggable: '.gf-chip',
                ghostClass: 'gf-chip-ghost',
                chosenClass: 'gf-chip-chosen',
                forceFallback: true,
                fallbackOnBody: true,
                onEnd(evt) {
                    setAddSiteDropHover(false);
                    const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                    const toSiteId   = parseInt(evt.to.dataset.gfSite, 10);
                    const newIdx = getReliableDropIndex(evt);
                    const oldIdx = evt.oldDraggableIndex ?? evt.oldIndex;
                    const fromSite = sites.find((s) => s.id === fromSiteId);
                    const toSite   = sites.find((s) => s.id === toSiteId);
                    if (!fromSite || !toSite) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    // Cross-group: unprotected chip dropped INTO jobs group (promote to "Currently Protected")
                    if (evt.to.classList.contains('gf-chip-group--jobs')) {
                        const workloadId = parseInt(evt.item.dataset.gfUnprotectedRow, 10);
                        const fromArr = Array.isArray(fromSite.unprotectedWorkloads) ? fromSite.unprotectedWorkloads : [];
                        const fromIdx = fromArr.findIndex((w) => w.id === workloadId);
                        if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                        const [wl] = fromArr.splice(fromIdx, 1);
                        const newJob = window.buildDefaultJobEntry('backup', wl.workloadType || 'vmware', wl.count || 50);
                        if (wl.customLabel) newJob.customLabel = wl.customLabel;
                        if (!Array.isArray(toSite.jobs)) toSite.jobs = [];
                        toSite.jobs.splice(Math.min(newIdx, toSite.jobs.length), 0, newJob);
                        emit(BUS_EVENTS.SITE_LIST_CHANGED);
                        window.syncProtectionTogglesFromJobs();
                        window.syncDesignOptionFields();
                        window.updateTotalInstances();
                        window.updateWorkloadValidationState();
                        window.syncNextStepAvailability();
                        window.updateGfSummaryCount();
                        scheduleRenderEstimate();
                        return;
                    }

                    // Normal: unprotected chip stays within unprotected group (same or cross-site)
                    if (fromSiteId === toSiteId && oldIdx === newIdx) return;
                    const workloadId = parseInt(evt.item.dataset.gfUnprotectedRow, 10);
                    const fromArr = Array.isArray(fromSite.unprotectedWorkloads) ? fromSite.unprotectedWorkloads : [];
                    const fromIdx = fromArr.findIndex((w) => w.id === workloadId);
                    if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                    const [moved] = fromArr.splice(fromIdx, 1);
                    if (!Array.isArray(toSite.unprotectedWorkloads)) toSite.unprotectedWorkloads = [];
                    toSite.unprotectedWorkloads.splice(Math.min(newIdx, toSite.unprotectedWorkloads.length), 0, moved);
                    emit(BUS_EVENTS.SITE_LIST_CHANGED);
                }
            });
            gfWlSortables.push(sortable);
        }

        // ── Source workloads (.swp-chip-list) ────────────────────────────────────
        // New unified source workload chips — draggable within and across sites.
        // data-gf-site is stamped by buildSourceWorkloadSectionMarkup in recalc.js.
        const swpList = zone.querySelector('.swp-chip-list');
        if (swpList) {
            const sortable = Sortable.create(swpList, {
                group: { name: 'gf-source-workloads', put: ['gf-source-workloads'] },
                animation: 150,
                handle: '.swp-chip-grip',
                draggable: '.swp-chip',
                ghostClass: 'gf-chip-ghost',
                chosenClass: 'gf-chip-chosen',
                forceFallback: true,
                fallbackOnBody: true,
                onEnd(evt) {
                    setAddSiteDropHover(false);
                    const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                    const toSiteId   = parseInt(evt.to.dataset.gfSite, 10);
                    const newIdx = getReliableDropIndex(evt);
                    const oldIdx = evt.oldDraggableIndex ?? evt.oldIndex;
                    if (fromSiteId === toSiteId && oldIdx === newIdx) return;

                    const fromSite = sites.find((s) => s.id === fromSiteId);
                    const toSite   = sites.find((s) => s.id === toSiteId);
                    if (!fromSite || !toSite) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    const workloadId = parseInt(evt.item.dataset.swpWorkloadId, 10);
                    if (!Array.isArray(fromSite.sourceWorkloads)) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                    const fromIdx = fromSite.sourceWorkloads.findIndex((w) => w.id === workloadId);
                    if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    const [moved] = fromSite.sourceWorkloads.splice(fromIdx, 1);
                    if (!Array.isArray(toSite.sourceWorkloads)) toSite.sourceWorkloads = [];
                    toSite.sourceWorkloads.splice(Math.min(newIdx, toSite.sourceWorkloads.length), 0, moved);

                    emit(BUS_EVENTS.SITE_LIST_CHANGED);
                    window.updateWorkloadValidationState?.();
                    window.syncNextStepAvailability?.();
                    scheduleRenderEstimate();
                }
            });
            gfWlSortables.push(sortable);
        }

        // ── Jobs ("Currently Protected") — one sortable per product section ─────
        // Jobs are split by workloadType into per-product .gf-chip-group--jobs containers
        // (VBR / Backup for Cloud / Containers / VB365). Each gets its own Sortable so
        // chips are draggable within and across sites. All share the same 'gf-jobs' group
        // name so unprotected→job promotions can still target any jobs container.
        const jobsGroups = zone.querySelectorAll('.gf-chip-group--jobs');
        jobsGroups.forEach((jobsGroup) => {
            jobsGroup.dataset.gfSite = String(site.id);
            const sortable = Sortable.create(jobsGroup, {
                group: { name: 'gf-jobs', put: ['gf-jobs', 'gf-unprotected-workloads'] },
                animation: 150,
                handle: '.gf-chip-grip',
                draggable: '.gf-chip',
                ghostClass: 'gf-chip-ghost',
                chosenClass: 'gf-chip-chosen',
                forceFallback: true,
                fallbackOnBody: true,
                onEnd(evt) {
                    setAddSiteDropHover(false);
                    const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                    const toSiteId   = parseInt(evt.to.dataset.gfSite, 10);
                    const newIdx = getReliableDropIndex(evt);
                    const oldIdx = evt.oldDraggableIndex ?? evt.oldIndex;
                    const fromSite = sites.find((s) => s.id === fromSiteId);
                    const toSite   = sites.find((s) => s.id === toSiteId);
                    if (!fromSite || !toSite) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                    // Cross-group: job chip dropped INTO unprotected group (demote to "Currently Unprotected")
                    if (evt.to.classList.contains('gf-chip-group--unprotected')) {
                        const jobId = parseInt(evt.item.dataset.gfJobRow, 10);
                        const fromArr = Array.isArray(fromSite.jobs) ? fromSite.jobs : [];
                        const fromIdx = fromArr.findIndex((j) => j.id === jobId);
                        if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                        const [job] = fromArr.splice(fromIdx, 1);
                        const newWl = window.buildDefaultUnprotectedWorkloadEntry(job.workloadType || 'vmware', job.count || 50);
                        if (job.customLabel) newWl.customLabel = job.customLabel;
                        if (!Array.isArray(toSite.unprotectedWorkloads)) toSite.unprotectedWorkloads = [];
                        toSite.unprotectedWorkloads.splice(Math.min(newIdx, toSite.unprotectedWorkloads.length), 0, newWl);
                        emit(BUS_EVENTS.SITE_LIST_CHANGED);
                        window.syncProtectionTogglesFromJobs();
                        window.syncDesignOptionFields();
                        window.updateTotalInstances();
                        window.updateWorkloadValidationState();
                        window.syncNextStepAvailability();
                        window.updateGfSummaryCount();
                        scheduleRenderEstimate();
                        return;
                    }

                    // Normal: job chip stays within jobs group (same or cross-site)
                    if (fromSiteId === toSiteId && oldIdx === newIdx) return;
                    const jobId = parseInt(evt.item.dataset.gfJobRow, 10);
                    const fromArr = Array.isArray(fromSite.jobs) ? fromSite.jobs : [];
                    const fromIdx = fromArr.findIndex((j) => j.id === jobId);
                    if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }
                    const [moved] = fromArr.splice(fromIdx, 1);
                    if (!Array.isArray(toSite.jobs)) toSite.jobs = [];
                    toSite.jobs.splice(Math.min(newIdx, toSite.jobs.length), 0, moved);
                    emit(BUS_EVENTS.SITE_LIST_CHANGED);
                }
            });
            gfWlSortables.push(sortable);
        });

        // ── Zone catch-all (receive-only) ──────────────────────────────────────
        // When a chip is dragged onto a site that has no matching chip-group yet
        // (e.g. dropping a wl chip onto a site with zero unprotected workloads),
        // neither the --wl nor --protected Sortable exists as a drop target.
        // This receive-only Sortable on the zone itself catches those drops and
        // auto-routes the chip to the correct data-model array by inspecting the
        // chip's dataset keys, then calls renderSiteList() to rebuild cleanly.
        // SortableJS gives priority to the deepest matching container, so when
        // the chip-group Sortable already exists, it handles the drop instead.
        //
        // NOTE: gf-unprotected-workloads and gf-jobs are intentionally NOT in
        // this put list. Their chip groups (.gf-chip-group--unprotected and
        // .gf-chip-group--jobs) are always rendered — even when empty — so
        // those sortables are always available as drop targets. Including them
        // here would cause the zone catch-all to compete with those chip groups,
        // which makes SortableJS place the drag ghost at zone level (visually
        // appearing "far right" outside any category box) rather than inside
        // the correct chip group where position tracking works correctly.
        const zoneCatchAll = Sortable.create(zone, {
            group: {
                name: 'gf-zone-catchall-' + site.id,
                pull: false, // nothing can be dragged OUT via this Sortable
                put: ['gf-workloads', 'gf-protected-workloads', 'gf-uncategorised-workloads'],
            },
            sort: false,
            // No items should be draggable FROM this container — it is receive-only.
            // Without this, SortableJS defaults draggable to '> *' which treats
            // .gf-chip-group divs as draggable items and intercepts mousedown events
            // before the chip-group sortables can register the drag.
            draggable: '.gf-nonexistent',
            forceFallback: true,
            fallbackOnBody: true,
            onEnd() { setAddSiteDropHover(false); },
            onAdd(evt) {
                setAddSiteDropHover(false);
                const item       = evt.item;
                const toSiteId   = parseInt(zone.dataset.gfSite, 10);
                const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                const toSite     = sites.find((s) => s.id === toSiteId);
                const fromSite   = sites.find((s) => s.id === fromSiteId);

                if (!toSite || !fromSite || fromSiteId === toSiteId) {
                    emit(BUS_EVENTS.SITE_LIST_CHANGED);
                    return;
                }

                if (item.dataset.gfChip !== undefined) {
                    // ── Unprotected workload ──
                    const rowId   = parseInt(item.dataset.gfChip, 10);
                    const fromIdx = fromSite.workloads.findIndex(
                        (w) => w.rowId === rowId && w.value && !isRepoMigrationWorkload(w.value)
                    );
                    if (fromIdx !== -1) {
                        const [wl] = fromSite.workloads.splice(fromIdx, 1);
                        toSite.workloads.push(wl);
                    }
                } else if (item.dataset.gfPwChip !== undefined) {
                    // ── Protected workload ──
                    const rowId  = parseInt(item.dataset.gfPwChip, 10);
                    const fromArr = Array.isArray(fromSite.protectedWorkloads) ? fromSite.protectedWorkloads : [];
                    const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                    if (fromIdx !== -1) {
                        const [wl] = fromArr.splice(fromIdx, 1);
                        if (!Array.isArray(toSite.protectedWorkloads)) toSite.protectedWorkloads = [];
                        toSite.protectedWorkloads.push(wl);
                    }
                } else if (item.dataset.gfUncChip !== undefined) {
                    // ── Uncategorised workload ──
                    const rowId   = parseInt(item.dataset.gfUncChip, 10);
                    const fromArr = Array.isArray(fromSite.uncategorisedWorkloads) ? fromSite.uncategorisedWorkloads : [];
                    const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                    if (fromIdx !== -1) {
                        const [wl] = fromArr.splice(fromIdx, 1);
                        if (!Array.isArray(toSite.uncategorisedWorkloads)) toSite.uncategorisedWorkloads = [];
                        toSite.uncategorisedWorkloads.push(wl);
                    }
                }
                // Note: gfUnprotectedRow and gfJobRow chips never reach the zone
                // catch-all because those groups are excluded from its put list —
                // their chip groups are always rendered and handle drops directly.

                emit(BUS_EVENTS.SITE_LIST_CHANGED);
            }
        });
        gfWlSortables.push(zoneCatchAll);
    });

    // ── Add-site drop target ───────────────────────────────────────────────────
    // Dragging any workload chip onto the "+ Add site" button creates a new site
    // and moves the chip into it automatically.
    const addSiteBtn = document.getElementById('gf-add-site-btn');
    if (addSiteBtn) {
        const sortable = Sortable.create(addSiteBtn, {
            group: {
                name: 'gf-drop-new-site',
                pull: false,
                put: ['gf-workloads', 'gf-protected-workloads', 'gf-uncategorised-workloads', 'gf-comp-chips', 'gf-unprotected-workloads', 'gf-jobs'],
            },
            sort: false,
            // Only treat .gf-chip elements as sortable items so the button's own SVG/text
            // content is ignored when calculating insertion position — ghost always lands first.
            draggable: '.gf-chip',
            forceFallback: true,
            fallbackOnBody: true,
            onAdd(evt) {
                setAddSiteDropHover(false);
                const item       = evt.item;
                const fromSiteId = parseInt(evt.from.dataset.gfSite, 10);
                const fromSite   = sites.find((s) => s.id === fromSiteId);

                // Create the new site first (without triggering a render yet)
                const newSite = window.createSite({});
                sites.push(newSite);

                if (fromSite) {
                    if (item.dataset.gfUncChip !== undefined) {
                        // ── Uncategorised workload → new site's uncategorised group ──
                        const rowId   = parseInt(item.dataset.gfUncChip, 10);
                        const fromArr = Array.isArray(fromSite.uncategorisedWorkloads) ? fromSite.uncategorisedWorkloads : [];
                        const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                        if (fromIdx !== -1) {
                            const [wl] = fromArr.splice(fromIdx, 1);
                            if (!Array.isArray(newSite.uncategorisedWorkloads)) newSite.uncategorisedWorkloads = [];
                            newSite.uncategorisedWorkloads.push(wl);
                        }
                    } else if (item.dataset.gfChip !== undefined && isRepoMigrationWorkload(item.dataset.gfChipValue)) {
                        // ── Vault / repository chip ──
                        const rowId   = parseInt(item.dataset.gfChip, 10);
                        const fromIdx = fromSite.workloads.findIndex(
                            (w) => w.rowId === rowId && isRepoMigrationWorkload(w.value)
                        );
                        if (fromIdx !== -1) {
                            const [wl] = fromSite.workloads.splice(fromIdx, 1);
                            newSite.workloads.push(wl);
                        }
                    } else if (item.dataset.gfChip !== undefined) {
                        // ── Unprotected workload ──
                        const rowId   = parseInt(item.dataset.gfChip, 10);
                        const fromIdx = fromSite.workloads.findIndex(
                            (w) => w.rowId === rowId && w.value && !isRepoMigrationWorkload(w.value)
                        );
                        if (fromIdx !== -1) {
                            const [wl] = fromSite.workloads.splice(fromIdx, 1);
                            newSite.workloads.push(wl);
                        }
                    } else if (item.dataset.gfPwChip !== undefined) {
                        // ── Protected workload ──
                        const rowId   = parseInt(item.dataset.gfPwChip, 10);
                        const fromArr = Array.isArray(fromSite.protectedWorkloads) ? fromSite.protectedWorkloads : [];
                        const fromIdx = fromArr.findIndex((w) => w.rowId === rowId && w.value);
                        if (fromIdx !== -1) {
                            const [wl] = fromArr.splice(fromIdx, 1);
                            newSite.protectedWorkloads = [wl];
                        }
                    } else if (item.dataset.gfServerTypeChip !== undefined) {
                        // ── Veeam server type chip (e.g. VBR, VB365, Kasten…) ──
                        // Move ALL servers of this type from the source site to the new site.
                        const typeKey = item.dataset.gfServerTypeChip;
                        if (Array.isArray(fromSite.veeamServers)) {
                            const moved   = fromSite.veeamServers.filter((s) => s.type === typeKey);
                            fromSite.veeamServers = fromSite.veeamServers.filter((s) => s.type !== typeKey);
                            if (!Array.isArray(newSite.veeamServers)) newSite.veeamServers = [];
                            newSite.veeamServers.push(...moved);
                        }
                    } else if (item.dataset.gfCompChip !== undefined) {
                        // ── Existing-environment component chip ──
                        const compKey   = item.dataset.gfCompChip;
                        const version   = item.dataset.gfCompVersion   || null;
                        const haPairId  = item.dataset.gfHaPairId      || null;
                        const slotIndex = item.dataset.gfCompSlotIndex !== undefined
                            ? parseInt(item.dataset.gfCompSlotIndex, 10)
                            : null;
                        if (lockedQtyFieldKeys.has(compKey)) {
                            // Locked-qty field (VBR, VRO, VONE, VB365 repos): move exactly one slot
                            window.moveOneLockedCompSlot(fromSite, newSite, compKey, version, haPairId, slotIndex);
                        } else if (fromSite.existingEnvironment && compKey in fromSite.existingEnvironment) {
                            if (!newSite.existingEnvironment) newSite.existingEnvironment = {};
                            newSite.existingEnvironment[compKey] = fromSite.existingEnvironment[compKey];
                            delete fromSite.existingEnvironment[compKey];
                        }
                    } else if (item.dataset.gfUnprotectedRow !== undefined) {
                        // ── "Currently Unprotected" chip ──
                        const workloadId = parseInt(item.dataset.gfUnprotectedRow, 10);
                        const fromArr = Array.isArray(fromSite.unprotectedWorkloads) ? fromSite.unprotectedWorkloads : [];
                        const fromIdx = fromArr.findIndex((w) => w.id === workloadId);
                        if (fromIdx !== -1) {
                            const [wl] = fromArr.splice(fromIdx, 1);
                            if (!Array.isArray(newSite.unprotectedWorkloads)) newSite.unprotectedWorkloads = [];
                            newSite.unprotectedWorkloads.push(wl);
                        }
                    } else if (item.dataset.gfJobRow !== undefined) {
                        // ── "Currently Protected" job chip ──
                        const jobId = parseInt(item.dataset.gfJobRow, 10);
                        const fromArr = Array.isArray(fromSite.jobs) ? fromSite.jobs : [];
                        const fromIdx = fromArr.findIndex((j) => j.id === jobId);
                        if (fromIdx !== -1) {
                            const [job] = fromArr.splice(fromIdx, 1);
                            if (!Array.isArray(newSite.jobs)) newSite.jobs = [];
                            newSite.jobs.push(job);
                        }
                    }
                }

                emit(BUS_EVENTS.SITE_LIST_CHANGED);
            }
        });
        gfWlSortables.push(sortable);
    }
}

// ── Vault Sortables ───────────────────────────────────────────────────────────

export function initVaultSortables() {
    const sitesContainer = document.getElementById('vault-sites');
    if (!sitesContainer) return;

    // Site-level reordering
    gfCanvasSortable = Sortable.create(sitesContainer, {
        animation: 150,
        handle: '.gf-site-grip',
        draggable: '.gf-site',
        ghostClass: 'gf-site-ghost',
        chosenClass: 'gf-site-chosen',
        onEnd(evt) {
            if (evt.oldIndex === evt.newIndex) return;
            const [moved] = sites.splice(evt.oldIndex, 1);
            sites.splice(evt.newIndex, 0, moved);
            emit(BUS_EVENTS.SITE_LIST_CHANGED);
        }
    });
    window.gfCanvasSortable = gfCanvasSortable;

    // Chip-level reordering and cross-site movement
    document.querySelectorAll('[data-vdc-vault-drop-zone]').forEach((zone) => {
        zone.dataset.siteId = zone.dataset.siteId; // ensure attribute is set
        const sortable = Sortable.create(zone, {
            group: { name: 'vault-chips', put: ['vault-chips'] },
            animation: 150,
            handle: '.gf-chip-grip',
            draggable: '.gf-chip',
            ghostClass: 'gf-chip-ghost',
            chosenClass: 'gf-chip-chosen',
            forceFallback: true,
            fallbackOnBody: true,
            onEnd(evt) {
                const fromSiteId = parseInt(evt.from.dataset.siteId, 10);
                const toSiteId   = parseInt(evt.to.dataset.siteId, 10);
                const newIdx = getReliableDropIndex(evt);
                const oldIdx = evt.oldDraggableIndex ?? evt.oldIndex;

                if (fromSiteId === toSiteId && oldIdx === newIdx) return;

                const fromSite = sites.find((s) => s.id === fromSiteId);
                const toSite   = sites.find((s) => s.id === toSiteId);
                if (!fromSite || !toSite) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                const rowId = parseInt(evt.item.dataset.siteVdcVaultRow, 10);
                const fromIdx = fromSite.workloads.findIndex(
                    (w) => w.rowId === rowId && isRepoMigrationWorkload(w.value)
                );
                if (fromIdx === -1) { emit(BUS_EVENTS.SITE_LIST_CHANGED); return; }

                const [wl] = fromSite.workloads.splice(fromIdx, 1);
                const vaultInTarget = toSite.workloads.filter((w) => isRepoMigrationWorkload(w.value));
                const insertAt  = Math.min(newIdx, vaultInTarget.length);
                const firstVault = toSite.workloads.findIndex((w) => isRepoMigrationWorkload(w.value));
                const absInsert  = firstVault === -1 ? toSite.workloads.length : firstVault + insertAt;
                toSite.workloads.splice(absInsert, 0, wl);

                emit(BUS_EVENTS.SITE_LIST_CHANGED);
            }
        });
        gfWlSortables.push(sortable);
    });
}

// ── Add-site drop-hover via pointermove ───────────────────────────────────────
// SortableJS forceFallback mode captures pointer events, making mouseenter/mouseleave unreliable.
// Use a single document-level pointermove listener instead — cheap because it no-ops when no drag is active.
document.addEventListener('pointermove', (e) => {
    if (!Sortable.active) return;
    const btn = document.getElementById('gf-add-site-btn');
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setAddSiteDropHover(e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom);
});
